import { escapeHTML } from '../../utils/escape.js';
import { formatMonto } from '../../utils/formatters.js';
import { MOTIVO_MAX_LEN } from '../../shared/timesheet-policy.js';

/**
 * Cuerpo del cajón "Otros descuentos" (spec liquidacion-otros-descuentos,
 * B1/B2). Función de render pura que escapa todos sus valores interpolados
 * (el shell sólo escapa el título). Mismo diseño que `AdelantoDrawerView.js`:
 * alta + lista activa/anulada; un descuento ya liquidado se refleja con el
 * error que devuelve el servidor (`descuento_liquidado`), nunca bloqueado acá.
 * La diferencia es el `concepto`, obligatorio, que sale en el comprobante.
 * @param {string|null|undefined} fechaSql
 * @returns {string}
 */
function formatFechaSql(fechaSql) {
    if (typeof fechaSql !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(fechaSql)) return '—';
    const [anio, mes, dia] = fechaSql.split('-');
    return `${dia}/${mes}/${anio}`;
}

const BTN = 'px-3 py-2 rounded-xl bg-brand text-white text-[11px] font-black uppercase tracking-widest shadow disabled:opacity-60 disabled:pointer-events-none';
const BTN_SECONDARY = 'px-3 py-1.5 rounded-xl border border-border-subtle text-text-muted text-[10px] font-black uppercase tracking-widest hover:text-text-primary';
const INPUT = 'w-full bg-base border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-brand';

/**
 * @param {{
 *   empleadoNombre: string,
 *   fecha?: string, monto?: string, concepto?: string,
 *   cargando: boolean,
 *   activos: Array<{id:string, fecha:string, monto:number, concepto:string}>,
 *   anulados: Array<{id:string, fecha:string, monto:number, concepto:string, anulado_motivo:string|null}>,
 *   error?: string|null,
 *   submitting?: boolean,
 *   anulandoId?: string|null,
 *   motivo?: string,
 *   anularError?: string|null,
 *   incierto?: boolean,
 *   mensaje?: string|null,
 * }} model
 * @returns {string}
 */
export function OtrosDescuentosDrawerView(model = {}) {
    const activos = model.activos ?? [];
    const anulados = model.anulados ?? [];
    const linea = (d) => `${formatFechaSql(d.fecha)} · ${escapeHTML(d.concepto ?? '')} · ${formatMonto(d.monto)}`;

    const filaActiva = (d) => `
        <li class="flex items-center justify-between gap-2 py-2 border-b border-border-subtle last:border-0" data-otro-descuento-activo data-id="${escapeHTML(d.id)}">
            <span class="text-xs text-text-primary">${linea(d)}</span>
            ${
                model.anulandoId === d.id
                    ? `
                <form data-action-form="otro-descuento-anular-confirmar" data-id="${escapeHTML(d.id)}" class="flex items-center gap-2">
                    <input type="text" name="motivo" value="${escapeHTML(model.motivo ?? '')}" required class="${INPUT}" placeholder="Motivo" />
                    <button type="submit" class="${BTN_SECONDARY}">Confirmar</button>
                    <button type="button" data-action="otro-descuento-anular-cancelar" class="${BTN_SECONDARY}">Cancelar</button>
                </form>
            `
                    : `<button type="button" data-action="otro-descuento-anular-abrir" data-id="${escapeHTML(d.id)}" class="${BTN_SECONDARY}">Anular</button>`
            }
        </li>
    `;

    const filaAnulada = (d) => `
        <li class="py-2 border-b border-border-subtle last:border-0 text-xs text-text-muted line-through" data-otro-descuento-anulado data-id="${escapeHTML(d.id)}">
            ${linea(d)} — ${escapeHTML(d.anulado_motivo ?? '')}
        </li>
    `;

    return `
        <div data-drawer-tipo="otro_descuento">
            <p class="text-xs font-bold text-text-muted mb-4">${escapeHTML(model.empleadoNombre ?? '')}</p>

            ${model.error ? `<div role="alert" class="mb-3 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs font-bold">${escapeHTML(model.error)}</div>` : ''}
            ${model.anularError ? `<div role="alert" class="mb-3 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs font-bold">${escapeHTML(model.anularError)}</div>` : ''}

            ${
                model.incierto
                    ? `
            <div role="alert" class="mb-6 px-3 py-3 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-xs font-bold" data-otro-descuento-incierto>
                <p class="mb-2">${escapeHTML(model.mensaje ?? '')}</p>
                <button type="button" data-action="tablero-otro-descuento-actualizar" class="${BTN}">Actualizar lista</button>
            </div>
            `
                    : `
            <form data-action-form="otro-descuento-crear" class="space-y-3 mb-6">
                <label class="block text-[11px] font-black uppercase tracking-widest text-text-muted">
                    Fecha
                    <input type="date" name="fecha" value="${escapeHTML(model.fecha ?? '')}" required class="${INPUT} mt-1" />
                </label>
                <label class="block text-[11px] font-black uppercase tracking-widest text-text-muted">
                    Monto
                    <input type="number" name="monto" step="0.01" min="0.01" value="${escapeHTML(model.monto ?? '')}" required class="${INPUT} mt-1" />
                </label>
                <label class="block text-[11px] font-black uppercase tracking-widest text-text-muted">
                    Concepto
                    <input type="text" name="concepto" maxlength="${MOTIVO_MAX_LEN}" value="${escapeHTML(model.concepto ?? '')}" required placeholder="Ej.: vaso roto" class="${INPUT} mt-1" />
                </label>
                <button type="submit" class="${BTN}" ${model.submitting ? 'disabled' : ''}>Registrar descuento</button>
            </form>
            `
            }

            <div>
                <h3 class="text-[11px] font-black uppercase tracking-widest text-text-muted mb-2">Otros descuentos activos</h3>
                ${model.cargando ? '<p class="text-xs text-text-muted">Cargando...</p>' : activos.length ? `<ul>${activos.map(filaActiva).join('')}</ul>` : '<p class="text-xs text-text-muted">Sin otros descuentos activos.</p>'}
            </div>

            ${
                anulados.length
                    ? `
                <div class="mt-4">
                    <h3 class="text-[11px] font-black uppercase tracking-widest text-text-muted mb-2">Anulados</h3>
                    <ul>${anulados.map(filaAnulada).join('')}</ul>
                </div>
            `
                    : ''
            }
        </div>
    `;
}
